import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    CircularProgress,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    IconButton,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { useCreateMapPoi } from '../services/appService'; // Assuming these hooks exist/will be created
import axios from 'axios'; // Import axios for direct API call

function GooglePlacesSearchModal({ open, onClose, onPoiAdded, appId }) {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchError, setSearchError] = useState('');
    const [addError, setAddError] = useState('');
    const [addedPoiIds, setAddedPoiIds] = useState(new Set()); // Track added POIs

    const createPoiHook = useCreateMapPoi();

    const handleSearch = useCallback(async () => {
        if (!query || !appId) return;
        setLoading(true);
        setSearchError('');
        setAddError('');
        setResults([]);
        setAddedPoiIds(new Set()); // Reset added status on new search

        try {
            // Direct API call to the new backend endpoint
            const response = await axios.get(`/api/apps/${appId}/map-pois/google-places-search`, {
                params: { query },
            });
            
            const searchResults = response.data; // Assuming the backend returns the mapped array
            
            setResults(searchResults || []);
            if (!searchResults || searchResults.length === 0) {
                 setSearchError('No results found for your query.');
            }
        } catch (err) {
            console.error("Error searching Google Places via backend:", err);
            const errorMsg = err.response?.data?.message || err.message || 'Failed to perform search.';
            setSearchError(errorMsg);
        } finally {
            setLoading(false);
        }
    }, [appId, query]); // Removed braveSearchHook dependency

    const handleAddPoi = useCallback(async (poi) => {
        if (!appId || !poi || addedPoiIds.has(poi.id)) return;

        setAddError('');
        // Use the fields mapped by the backend from Google Places API
        const newPoiData = {
            title: poi.title || 'Untitled POI', 
            // Use address as description or add a separate description field if needed
            description: poi.address || 'No address provided', 
            latitude: poi.latitude,
            longitude: poi.longitude,
            category: poi.category || 'Uncategorized', 
            // Include address, phone, website if your POI schema supports them
            address: poi.address || '', 
            phone: poi.phone || '',
            website: poi.website || '',
        };

        // Validate essential fields (lat/lon)
        if (newPoiData.latitude === undefined || newPoiData.longitude === undefined) {
            setAddError(`Cannot add "${newPoiData.title}": Missing location data.`);
            return;
        }

        try {
            const createPoi = createPoiHook; // Get the function from the hook
             if (typeof createPoi !== 'function') {
                 console.error("createPoiHook did not return a function.");
                 throw new Error("Create POI function not available.");
             }
            await createPoi(appId, newPoiData);
            setAddedPoiIds(prev => new Set(prev).add(poi.id)); 
            onPoiAdded(); // Callback to indicate a POI was added
        } catch (err) {
            console.error("Error adding POI:", err);
            const errorMsg = err.response?.data?.message || err.message || `Failed to add POI: ${newPoiData.title}`;
            setAddError(errorMsg);
        }
    }, [appId, createPoiHook, onPoiAdded, addedPoiIds]);

    const handleClose = () => {
        setQuery('');
        setResults([]);
        setSearchError('');
        setAddError('');
        setLoading(false);
        setAddedPoiIds(new Set());
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
            <DialogTitle>AI POI Filling (Google Places Search)</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    <TextField
                        label="Search Google Places (e.g., 'restaurants near Brandenburg Gate')"
                        variant="outlined"
                        fullWidth
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        disabled={loading}
                        onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    />
                    <Button
                        variant="contained"
                        onClick={handleSearch}
                        disabled={loading || !query}
                        sx={{ flexShrink: 0 }}
                    >
                        {loading ? <CircularProgress size={24} /> : 'Search'}
                    </Button>
                </Box>

                {searchError && <Alert severity="warning" sx={{ mb: 2 }}>{searchError}</Alert>}
                {addError && <Alert severity="error" sx={{ mb: 2 }}>{addError}</Alert>}

                {results.length > 0 && (
                    <TableContainer component={Paper}>
                        <Table sx={{ minWidth: 750 }} aria-label="google places search results table">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{width: '25%'}}>Name</TableCell>
                                    <TableCell sx={{width: '35%'}}>Address</TableCell>
                                    <TableCell sx={{width: '15%'}}>Category</TableCell>
                                    <TableCell sx={{width: '15%'}}>Lat/Lon</TableCell>
                                    <TableCell sx={{width: '10%'}} align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {results.map((poi) => (
                                    <TableRow
                                        key={poi.id}
                                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                    >
                                        <TableCell component="th" scope="row">
                                            {poi.title || 'N/A'}
                                            {poi.website && 
                                                <Typography variant="caption" display="block">
                                                    <a href={poi.website} target="_blank" rel="noopener noreferrer">Website</a>
                                                </Typography>
                                            }
                                            {poi.phone && 
                                                <Typography variant="caption" display="block">{poi.phone}</Typography>
                                            }
                                        </TableCell>
                                        <TableCell>{poi.address || 'N/A'}</TableCell>
                                        <TableCell>{poi.category || 'N/A'}</TableCell>
                                        <TableCell>
                                            {poi.latitude !== undefined && poi.longitude !== undefined
                                                ? `${poi.latitude.toFixed(5)}, ${poi.longitude.toFixed(5)}`
                                                : 'N/A'}
                                        </TableCell>
                                        <TableCell align="right">
                                            <IconButton
                                                color="primary"
                                                onClick={() => handleAddPoi(poi)}
                                                disabled={addedPoiIds.has(poi.id)}
                                                title={addedPoiIds.has(poi.id) ? "Already Added" : "Add POI"}
                                            >
                                                <AddCircleOutlineIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
                 {loading && !results.length && ( // Show loading indicator only if no results yet
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                    </Box>
                )}
                 {!loading && !results.length && !searchError && (
                     <Typography variant="body1" sx={{ textAlign: 'center', p: 3 }}>
                         Enter a query and click Search to find POIs using Google Places.
                     </Typography>
                 )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}

GooglePlacesSearchModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onPoiAdded: PropTypes.func.isRequired,
    appId: PropTypes.string.isRequired,
};

export default GooglePlacesSearchModal; 